import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Search, Plus, ChevronLeft, ChevronRight, UploadCloud } from 'lucide-react';
import { Button, Heading, Text, Select } from '../../../components';
import { CarePackagesSkeleton } from '../../../skeletons';
import { domiciliaryPackagesService, domiciliaryScheduleService } from '../../../services';
import { localDateKey, todayKey } from '../../../utils/datetime';
import {
  enqueueChange as enqueueChangeAction,
  removeChange,
  setChanges,
  clearChanges,
} from '../../../store/domiciliaryPackagesDraftSlice';
import { applyPackagesDraft } from './applyPackagesDraft';

import DateSelector from './DateSelector';
import PackageClientView from './PackageClientView';
import PackageCarerView from './PackageCarerView';
import PackageWeekView from './PackageWeekView';
import AssignPackageModal from './AssignPackageModal';
import VisitDetailsModal from './VisitDetailsModal';
import PackageFormModal from './PackageFormModal';
import CreateZoneModal from './CreateZoneModal';
import AvailabilityModal from './AvailabilityModal';
import PublishReviewModal from './PublishReviewModal';

const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const weekOf = (dateStr) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const base = new Date(y, m - 1, d);
  const dow = base.getDay();
  const monday = new Date(base); monday.setDate(base.getDate() - dow + (dow === 0 ? -6 : 1));
  return Array.from({ length: 7 }, (_, i) => { const x = new Date(monday); x.setDate(monday.getDate() + i); return x; });
};

const Chip = ({ children, tone = 'default' }) => {
  const tones = { default: 'bg-white dark:bg-[#31373f] text-[#586a82] dark:text-gray-300 border-[#E2E8F0] dark:border-gray-700', alert: 'bg-purple-50 dark:bg-purple-900/20 text-purple-600 dark:text-purple-300 border-purple-100 dark:border-purple-900/30' };
  return <span className={`inline-flex items-center gap-1 rounded-lg border px-2.5 py-1 text-[11px] font-semibold shadow-sm ${tones[tone]}`}>{children}</span>;
};

const STATUS_MATCH = {
  all: () => true,
  unassigned: (s) => s === 'unassigned',
  assigned: (s) => s === 'scheduled' || s === 'completed' || s === 'missed',
  live: (s) => s === 'in_progress',
};

const CarePackagesScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const changes = useSelector((s) => s.domiciliaryPackagesDraft.changes);
  const [date, setDate] = useState(todayKey());
  const [view, setView] = useState('client');   // client | carer
  const [mode, setMode] = useState('day');       // day | week
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState('');
  const [zoneFilter, setZoneFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const [assignVisit, setAssignVisit] = useState(null);
  const [detailVisit, setDetailVisit] = useState(null);
  const [modalDate, setModalDate] = useState(date); // the date a visit popup acts on
  const [formOpen, setFormOpen] = useState(false);
  const [formPackageId, setFormPackageId] = useState(null);
  const [formFromDate, setFormFromDate] = useState(null);
  const [editZone, setEditZone] = useState(null);
  const [availabilityCarer, setAvailabilityCarer] = useState(null); // carer whose availability is being added ({id,name,shift})
  const [publishOpen, setPublishOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Unassigned → assign popup; assigned/done/etc → visit details popup.
  // `cellDate` is the column's date in week view; falls back to the page date.
  const openVisit = (visit, cellDate) => {
    setModalDate(cellDate || date);
    const v = { ...visit, client: visit.client || (visit.client_name ? { name: visit.client_name } : undefined) };
    if (visit.status === 'unassigned') setAssignVisit(v);
    else setDetailVisit({ ...v, _carerId: v._carerId || v.carers?.[0]?.id || null });
  };

  const fetchData = useCallback(async (d, v, m) => {
    setLoading(true);
    try {
      const res = m === 'week'
        ? await domiciliaryPackagesService.getPackagesWeek(d, v)
        : await domiciliaryPackagesService.getPackages(d, v);
      if (res.success) setData({ ...res.data, _mode: m });
    } catch (e) {
      toast.error('Failed to load care packages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(date, view, mode); }, [date, view, mode, fetchData]);
  const refresh = () => fetchData(date, view, mode);

  // The board shows the saved snapshot with unpublished edits applied on top.
  const viewData = useMemo(() => applyPackagesDraft(data, changes), [data, changes]);
  const pendingCount = changes.length;

  // ── Draft queue helpers ─────────────────────────────────────────────────
  const draftId = () => `pchg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  const enqueue = (change) => dispatch(enqueueChangeAction({ id: draftId(), ...change }));
  const removeOneChange = (id) => dispatch(removeChange(id));

  const carers = data?.carers || [];
  const zonesForModals = useMemo(() => (data?.zones || []).filter((z) => z.id != null).map((z) => ({ id: z.id, name: z.name, color: z.color, postcode_prefixes: z.postcode_prefixes, carer_ids: [] })), [data]);
  const weekDates = useMemo(() => weekOf(date), [date]);
  const a = weekDates[0]; const b = weekDates[6];
  const weekLabel = a && b ? `${MON[a.getMonth()]} ${a.getDate()} – ${a.getMonth() === b.getMonth() ? '' : MON[b.getMonth()] + ' '}${b.getDate()}` : '';
  const inCurrentWeek = weekDates.some((d) => localDateKey(d) === todayKey());
  const shiftWeek = (delta) => {
    const [y, m, d] = date.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    dt.setDate(dt.getDate() + delta * 7);
    setDate(localDateKey(dt));
  };

  // Client-side filtering of the already-loaded data (small dataset). Handles
  // both day and week shapes; bails out until the loaded data matches the active
  // mode + view (avoids a stale-shape crash during a switch).
  const displayZones = useMemo(() => {
    if (!viewData || viewData.view !== view || viewData._mode !== mode) return [];
    let zones = viewData.zones || [];
    if (zoneFilter !== 'all') zones = zones.filter((z) => String(z.id) === String(zoneFilter));
    const q = search.trim().toLowerCase();

    if (mode === 'week') {
      if (view === 'client') {
        return zones.map((z) => ({ ...z, clients: (z.clients || []).filter((c) => !q || c.name.toLowerCase().includes(q)) }))
          .filter((z) => z.clients.length > 0);
      }
      return zones.map((z) => ({ ...z, carers: (z.carers || []).filter((c) => !q || c.name.toLowerCase().includes(q)) }))
        .filter((z) => z.carers.length > 0 || Object.keys(z.unassigned_days || {}).length > 0);
    }

    const matchVisit = (v) => (!q || v.client.name.toLowerCase().includes(q) || (v.package_name || '').toLowerCase().includes(q))
      && STATUS_MATCH[statusFilter](v.status);
    if (view === 'client') {
      return zones.map((z) => ({ ...z, visits: (z.visits || []).filter(matchVisit) })).filter((z) => z.visits.length > 0);
    }
    return zones.map((z) => ({
      ...z,
      carers: (z.carers || []).map((c) => ({ ...c, visits: (c.visits || []).filter(matchVisit) }))
        .filter((c) => !q || c.name.toLowerCase().includes(q) || c.visits.length > 0),
      unassigned: (z.unassigned || []).filter(matchVisit),
    })).filter((z) => z.carers.length > 0 || z.unassigned.length > 0);
  }, [viewData, view, mode, search, zoneFilter, statusFilter]);

  // ── Draft staging ────────────────────────────────────────────────────────
  const carerById = (id) => carers.find((c) => String(c.id) === String(id)) || null;

  // Assign popup commit (carers, scope) — assignVisit is the clicked visit.
  const stageAssign = (carerIds, scope) => {
    const v = assignVisit;
    if (!v) return;
    const names = carerIds.map((c) => c.name).join(' & ');
    enqueue({
      type: 'assign',
      dedupKey: `assign:${v.assignment_id}:${modalDate}`,
      payload: { assignment_id: v.assignment_id, date: modalDate, carerIds, scope, visit: v },
      label: carerIds.length === 0
        ? `Unassign ${v.client?.name || v.package_name}`
        : `${v.client?.name || v.package_name} → ${names} (${scope === 'always' ? 'every day' : 'this visit'})`,
    });
  };

  // Drag-and-drop assign (By-Carer day + week): assign the carer for THAT day
  // (scope 'today'). A per-day override always wins over the package default in
  // resolveCarers, so this reliably clears any "unassigned for the day" override
  // and reflects after publish. (Use the Assign popup for an every-day default.)
  const stageDropAssign = (assignmentId, carerId, day) => {
    const c = carerById(carerId);
    const onDate = day || date;
    enqueue({
      type: 'assign',
      dedupKey: `assign:${assignmentId}:${onDate}`,
      payload: { assignment_id: assignmentId, date: onDate, carerIds: [{ id: carerId, name: c?.name, initials: c?.initials }], scope: 'today', day: day || undefined },
      label: `${c?.name || 'Carer'} → package · ${onDate}`,
    });
    toast.success('Assignment added to draft');
  };

  // VisitDetails: unassign (this visit) / stop the package from this day.
  const stageUnassign = (visit) => {
    if (!visit) return;
    enqueue({
      type: 'assign',
      dedupKey: `assign:${visit.assignment_id}:${modalDate}`,
      payload: { assignment_id: visit.assignment_id, date: modalDate, carerIds: [], scope: 'today', visit },
      label: `Unassign ${visit.client?.name || visit.package_name} · ${visit.start}`,
    });
  };
  const stageStop = (assignmentId, ctx = {}) => {
    enqueue({
      type: 'stopVisit',
      dedupKey: `stopVisit:${assignmentId}:${modalDate}`,
      payload: { assignment_id: assignmentId, date: modalDate },
      label: `Stop ${ctx.name || 'package'} from ${modalDate}`,
    });
  };

  // Replay one queued change against the API.
  const syncChange = (c) => {
    const p = c.payload;
    if (c.type === 'assign') {
      return domiciliaryScheduleService.assignCarer({
        assignment_id: p.assignment_id, date: p.date, carer_ids: p.carerIds.map((x) => x.id), scope: p.scope,
      });
    }
    if (c.type === 'stopVisit') {
      return domiciliaryScheduleService.stopFromDate({ assignment_id: p.assignment_id, date: p.date });
    }
    return Promise.resolve();
  };

  // Publish = replay the queue in order, then notify carers + reload. Failed
  // changes stay queued so nothing is lost.
  const handlePublish = async () => {
    if (changes.length === 0) { toast('No changes to publish'); return; }
    setPublishing(true);
    const total = changes.length;
    const failed = [];
    for (const c of changes) {
      try {
        const res = await syncChange(c);
        if (res && res.success === false) failed.push(c);
      } catch (err) { failed.push(c); }
    }
    try { await domiciliaryScheduleService.publishSchedule(); } catch (_) { /* notify is best-effort */ }

    if (failed.length === 0) {
      dispatch(clearChanges());
      toast.success(`Published ${total} change${total === 1 ? '' : 's'} to carers`);
      setPublishOpen(false);
    } else {
      dispatch(setChanges(failed));
      toast.error(`${total - failed.length} of ${total} published — ${failed.length} failed and stay in draft`);
    }
    await refresh();
    setPublishing(false);
  };

  // Warn before leaving with unpublished changes (drafts live only in memory).
  useEffect(() => {
    if (changes.length === 0) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [changes.length]);

  const openForm = (id = null, from = null) => { setFormPackageId(id); setFormFromDate(from); setFormOpen(true); };
  const summary = viewData?.summary || {};

  if (loading && !data) return <CarePackagesSkeleton />;

  return (
    <div className="w-full pb-20 font-sans">
      {/* Sticky compact header */}
      <div className="sticky top-0 z-30 bg-[#EEF2F6] dark:bg-[#1b1f26] -mx-6 md:-mx-8 px-6 md:px-8 pt-4 -mt-6 md:-mt-8 pb-2.5 border-b border-[#E2E8F0] dark:border-gray-800">
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-3">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/manager/domiciliary-schedule')} className="grid h-9 w-9 place-items-center rounded-full bg-white dark:bg-[#31373f] text-gray-500 dark:text-gray-400 shadow-sm hover:bg-gray-50 dark:hover:bg-[#31373f]">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <div>
              <Heading level={2} className="leading-tight">Care Packages</Heading>
              <Text variant="caption" as="p">Plan visits by client, carer, travel zone and travel area</Text>
            </div>
          </div>

          <div className="flex items-center gap-2.5 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search client or package…"
                className="h-10 w-[230px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] dark:text-gray-200 pl-9 pr-3 text-sm shadow-sm focus:border-blue-300 focus:outline-none" />
            </div>
            <Select
              className="w-[150px]"
              value={zoneFilter}
              onChange={setZoneFilter}
              options={[
                { value: 'all', label: 'All zones' },
                ...(data?.zones || []).filter((z) => z.id != null).map((z) => ({ value: String(z.id), label: z.name })),
              ]}
            />
            {/* Day / Week toggle */}
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] p-1 shadow-sm">
              <button onClick={() => setMode('day')} className={`rounded-md px-3 py-1.5 text-[13px] font-bold ${mode === 'day' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>Day</button>
              <button onClick={() => setMode('week')} className={`rounded-md px-3 py-1.5 text-[13px] font-bold ${mode === 'week' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>Week</button>
            </div>
            <Button variant="primary" leftIcon={<Plus className="h-4 w-4" />} onClick={() => openForm(null)}>
              New Package
            </Button>
            <Button
              variant="primary"
              leftIcon={<UploadCloud className="h-4 w-4" />}
              loading={publishing}
              disabled={pendingCount === 0}
              onClick={() => setPublishOpen(true)}
              title={pendingCount === 0 ? 'No unpublished changes' : `Review & publish ${pendingCount} change(s)`}
            >
              {pendingCount > 0 ? `Publish (${pendingCount})` : 'Publish'}
            </Button>
          </div>
        </div>

        {/* Tools row: perspective + status filter + summary */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-2.5">
          <div className="flex items-center gap-2.5">
            <div className="flex rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#31373f] p-1 shadow-sm">
              <button onClick={() => setView('client')} className={`rounded-md px-3 py-1.5 text-[13px] font-bold ${view === 'client' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>By Client</button>
              <button onClick={() => setView('carer')} className={`rounded-md px-3 py-1.5 text-[13px] font-bold ${view === 'carer' ? 'bg-blue-600 text-white' : 'text-gray-500 dark:text-gray-400'}`}>By Carer</button>
            </div>
            {mode === 'day' && (
              <Select
                className="w-[170px]"
                size="sm"
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: 'all', label: 'All packages' },
                  { value: 'unassigned', label: 'Needs assignment' },
                  { value: 'assigned', label: 'Assigned' },
                  { value: 'live', label: 'In progress' },
                ]}
              />
            )}
            {/* Week navigation (mirrors the duty roster) — change weeks here */}
            <div className="flex items-center gap-2">
              <button onClick={() => shiftWeek(-1)} className="p-1 rounded-md text-[#64748B] dark:text-gray-400 hover:bg-black/5 transition"><ChevronLeft className="w-5 h-5" /></button>
              <span className="text-[13px] font-semibold text-[#334155] dark:text-gray-200 whitespace-nowrap">Week of {weekLabel}</span>
              <button onClick={() => shiftWeek(1)} className="p-1 rounded-md text-[#64748B] dark:text-gray-400 hover:bg-black/5 transition"><ChevronRight className="w-5 h-5" /></button>
              {!inCurrentWeek && (
                <button onClick={() => setDate(todayKey())} className="ml-1 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[12px] font-bold text-white bg-[#2563EB] hover:bg-blue-700 transition shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-white" /> Today
                </button>
              )}
            </div>
          </div>
          {mode === 'day' && (
            <div className="flex items-center gap-1.5 flex-wrap">
              <Chip><b className="text-[#23334b] dark:text-gray-100">{summary.packages || 0}</b> packages</Chip>
              <Chip><b className="text-[#23334b] dark:text-gray-100">{summary.travel_areas || 0}</b> travel areas</Chip>
              {summary.need_assignment > 0 && <Chip tone="alert"><b>{summary.need_assignment}</b> need assignment</Chip>}
            </div>
          )}
        </div>

        {mode === 'day' && <DateSelector weekDates={weekDates} date={date} onChange={setDate} />}
      </div>

      {/* Board */}
      <div className="mt-4">
        {mode === 'week'
          ? <PackageWeekView zones={displayZones} days={data?.days || []} view={view} onOpen={openVisit} onAssign={stageDropAssign} />
          : view === 'client'
            ? <PackageClientView zones={displayZones} onOpen={openVisit} onEditZone={setEditZone} />
            : <PackageCarerView zones={displayZones} onOpen={openVisit} onAssign={(assignmentId, carerId) => stageDropAssign(assignmentId, carerId)} onEditAvailability={setAvailabilityCarer} />}
      </div>

      {/* Modals */}
      <AssignPackageModal
        visit={assignVisit}
        date={modalDate}
        lockAlways
        stageAssign={stageAssign}
        onClose={() => setAssignVisit(null)}
        onEditPackage={(id) => { setAssignVisit(null); openForm(id); }}
      />
      <VisitDetailsModal
        visit={detailVisit}
        date={modalDate}
        onClose={() => setDetailVisit(null)}
        stageStop={stageStop}
        stageUnassign={stageUnassign}
        onEditFromDay={(id) => { setDetailVisit(null); openForm(id, modalDate); }}
      />
      {formOpen && (
        <PackageFormModal
          key={`${formPackageId || 'new'}-${formFromDate || 'full'}`}
          open
          packageId={formPackageId}
          fromDate={formFromDate}
          zones={zonesForModals}
          carers={carers}
          defaultDate={date}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); refresh(); }}
        />
      )}
      {editZone && (
        <CreateZoneModal
          key={editZone.id}
          open
          zone={{ ...editZone, carer_ids: editZone.carer_ids || [] }}
          carers={carers}
          zones={data?.zones || []}
          onClose={() => setEditZone(null)}
          onSaved={() => { setEditZone(null); refresh(); }}
        />
      )}
      {availabilityCarer && (
        <AvailabilityModal
          open
          carers={carers}
          zones={zonesForModals}
          defaultDate={date}
          defaultEmployeeId={availabilityCarer.id}
          defaultStart={availabilityCarer.shift?.start}
          defaultEnd={availabilityCarer.shift?.end}
          onClose={() => setAvailabilityCarer(null)}
          onSaved={() => { setAvailabilityCarer(null); refresh(); }}
        />
      )}
      <PublishReviewModal
        open={publishOpen}
        changes={changes}
        publishing={publishing}
        onPublish={handlePublish}
        onRemove={removeOneChange}
        onClose={() => setPublishOpen(false)}
      />
    </div>
  );
};

export default CarePackagesScreen;
